/**
 * B16 — pure CURSORSIZE / CURSORCOLOR helpers for the HTML crosshair.
 *
 * Lives in its own file (not in `overlayLayer.tsx`) so the React-
 * refresh lint rule (`only-export-components`) stays satisfied —
 * `overlayLayer.tsx` exports React components, this file exports pure
 * math / colour utilities that are independent of the component tree.
 */

/**
 * Compute the half-length of the CURSORSIZE crosshair lines in CSS
 * pixels. AutoCAD parity: percentage of the *smaller* viewport
 * dimension, applied to the half-length so the visible total length is
 * `2 × halfLength = pct × min(w, h) / 100`.
 *
 * Pure helper, exported for unit tests. Clamps `pct` into the AutoCAD
 * documented range 1..100 to defend against malformed sysvar input.
 *
 * Reference: Autodesk CURSORSIZE (2025) — "size of the crosshair as a
 * percentage of the screen size".
 */
export function crosshairHalfLengthPx(
  pct: number,
  viewportWidth: number,
  viewportHeight: number,
): number {
  if (!Number.isFinite(pct)) return 0;
  const clamped = Math.max(1, Math.min(100, pct));
  const minDim = Math.max(0, Math.min(viewportWidth, viewportHeight));
  // Half-length: pct/100 * minDim / 2.
  return (clamped / 100) * minDim * 0.5;
}

/**
 * Resolve a CSS colour string for the crosshair from a CURSORCOLOR ACI
 * index. AutoCAD's default (index 7) auto-flips between white and black
 * to maximise contrast against the current viewport background — we
 * keep parity by returning the canonical "muted contrast" colour
 * (`rgba(170,170,170,.45)`) for index 7. Other ACI indices map to a
 * small palette subset (1..6 are the standard colours; 0 = ByBlock,
 * 256 = ByLayer — both fall through to the contrast default).
 *
 * The mapping is intentionally minimal: AutoCAD allows the user to pick
 * any ACI index for the crosshair via OPTIONS → Display, but the
 * vast majority of users keep CURSORCOLOR=7 and the contrast default is
 * what every screenshot in the parity corpus shows.
 */
export function cursorColorCssFromAci(aci: number): string {
  switch (aci) {
    case 1:
      return "rgba(255, 0, 0, 0.65)";
    case 2:
      return "rgba(255, 255, 0, 0.65)";
    case 3:
      return "rgba(0, 255, 0, 0.65)";
    case 4:
      return "rgba(0, 255, 255, 0.65)";
    case 5:
      return "rgba(0, 0, 255, 0.65)";
    case 6:
      return "rgba(255, 0, 255, 0.65)";
    case 7:
    case 0:
    case 256:
    default:
      return "rgba(170, 170, 170, 0.45)";
  }
}
